import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Send, Loader2, CheckCircle } from 'lucide-react';
import { API } from '../App';
import { toast } from 'sonner';

const SERVICES_LIST = [
  { id: 'proj-mgmt', name: 'Project Management' },
  { id: 'voip', name: 'VoIP Telephony Services' },
  { id: 'ms365', name: 'Microsoft 365 Management' },
  { id: 'google-workspace', name: 'Google Workspace Management' },
  { id: 'network', name: 'Network Infrastructure' },
  { id: 'security', name: 'Cybersecurity Services' },
  { id: 'remote-support', name: 'Remote IT Support' },
  { id: 'backup', name: 'Backup & Disaster Recovery' },
  { id: 'general', name: 'General Inquiry' },
];

export const ContactForm = ({ onClose, preselectedService }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service_interest: preselectedService || '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/contact/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit inquiry');
      }

      setSubmitted(true);
      toast.success('Inquiry submitted successfully!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12"
      >
        <CheckCircle className="w-16 h-16 text-[#00FF94] mx-auto mb-4" />
        <h3 className="text-2xl font-bold mb-2">Thank You!</h3>
        <p className="text-[#A1A1AA] mb-6">
          We've received your inquiry and will contact you within 24 hours.
        </p>
        {onClose && (
          <Button onClick={onClose} variant="outline" className="border-[#27272A]">
            Close
          </Button>
        )}
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Your name"
            required
            className="bg-[#0A0A0A] border-[#27272A] focus:border-[#00F0FF]"
            data-testid="contact-name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your@email.com"
            required
            className="bg-[#0A0A0A] border-[#27272A] focus:border-[#00F0FF]"
            data-testid="contact-email"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="company">Company</Label>
          <Input
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            placeholder="Your company name"
            className="bg-[#0A0A0A] border-[#27272A] focus:border-[#00F0FF]"
            data-testid="contact-company"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+44 (0) 123 456 7890"
            className="bg-[#0A0A0A] border-[#27272A] focus:border-[#00F0FF]"
            data-testid="contact-phone"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="service_interest">Service Interest</Label>
        <Select 
          value={formData.service_interest} 
          onValueChange={(value) => setFormData(prev => ({ ...prev, service_interest: value }))}
        >
          <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]" data-testid="contact-service">
            <SelectValue placeholder="Select a service" />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-[#27272A]">
            {SERVICES_LIST.map((service) => (
              <SelectItem key={service.id} value={service.id}>
                {service.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message">Message *</Label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Tell us about your IT needs..."
          required
          rows={5}
          className="bg-[#0A0A0A] border-[#27272A] focus:border-[#00F0FF] resize-none"
          data-testid="contact-message"
        />
      </div>

      <div className="flex items-center justify-end">
        <div className="flex gap-3">
          {onClose && (
            <Button type="button" onClick={onClose} variant="outline" className="border-[#27272A]">
              Cancel
            </Button>
          )}
          <Button 
            type="submit" 
            disabled={loading}
            className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 btn-glow"
            data-testid="contact-submit"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            {loading ? 'Sending...' : 'Send Inquiry'}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ContactForm;
